import { type ReactElement, useEffect, useMemo, useRef, useState } from 'react';
import { type HemerotecaSearchValues } from '../HemerotecaSearchForm/config';
import classNames from 'classnames';
import { useStrapiApi, useStrapiParams } from '@/hooks';
import { getHemerotecaResults } from '@/services/strapi';
import type { HemerotecaType, StrapiListRequest, StrapiListResponse } from '@/interfaces/strapi';
import CTNPagination from '@ctn/ui/components/ctn-pagination';
import { Loading } from '@/components';
import CTNIcon from '@ctn/ui/components/ctn-icon/icon';

type HemerotecaResultsProps = {
	readonly className?: string;
	readonly filters: HemerotecaSearchValues | null;
	readonly initialPage?: number;
	readonly pageSize?: number;
};

const classNameComponent = 'c-cnm-hemeroteca-results';

export const buildHemerotecaAdditionalFilters = (values: HemerotecaSearchValues | null) => {
	if (!values) return undefined;

	const f: Record<string, any> = {};

	if (values.autor?.trim()) f.autor = { $containsi: values.autor.trim() };
	if (values.titulo?.trim()) f.titulo = { $containsi: values.titulo.trim() };
	if (values.tomo) f.tomo = { $eq: values.tomo };
	if (values.year) f.fechaPublicacion = { $eq: values.year };

	return Object.keys(f).length ? f : undefined;
};

export const HemerotecaResults = ({
	className,
	filters,
	initialPage = 1,
	pageSize = 4,
}: HemerotecaResultsProps): ReactElement => {
	const wrapperClassName = classNames(classNameComponent, className);
	const [page, setPage] = useState(initialPage);

	const filtersKey = useMemo(() => JSON.stringify(filters ?? {}), [filters]);
	useEffect(() => {
		setPage(initialPage);
	}, [filtersKey, initialPage]);

	const additionalFilters = useMemo(() => buildHemerotecaAdditionalFilters(filters), [filters]);

	const params = useStrapiParams({
		page,
		pageSize,
		sort: 'fechaPublicacion:desc',
		additionalFilters,
		populate: { file: true },
	});

	const { data, loading } = useStrapiApi<StrapiListResponse<HemerotecaType>, StrapiListRequest>(
		getHemerotecaResults,
		{
			autoFetch: Boolean(filters),
			params,
		},
	);

	const results = data?.list ?? [];
	const pagination = data?.pagination;
	const paginationRef = useRef<any>(null);

	useEffect(() => {
		if (pagination && paginationRef.current?.goToPage) {
			paginationRef.current.goToPage(pagination.page ?? page);
		}
		if (pagination && typeof pagination.page === 'number' && pagination.page !== page) {
			setPage(pagination.page);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [pagination?.page]);

	const handlePageChange = (newPage: number) => {
		if (newPage === page) return;
		setPage(newPage);
		window.scrollTo({ top: 0, behavior: 'smooth' });
	};

	return (
		<section className={wrapperClassName}>
			{!filters && (
				<div className={`${classNameComponent}__content`}>
					<p className={`${classNameComponent}__hint`}>Utiliza el buscador para ver conferencias.</p>
				</div>
			)}

			{filters && (
				<>
					{loading && (
						<div className={`${classNameComponent}__loading`}>
							<Loading />
						</div>
					)}

					{!loading && results.length === 0 && (
						<div className={`${classNameComponent}__content`}>
							<p className={`${classNameComponent}__hint`}>No hay resultados con esos criterios.</p>
						</div>
					)}

					{!loading && results.length > 0 && (
						<>
							<ul className={`${classNameComponent}__list`}>
								{results.map((item) => (
									<li key={item.id} className={`${classNameComponent}__item`}>
										<div className={`${classNameComponent}__item-title`}>
											<h5>{item.titulo}</h5>
										</div>

										<div className={`${classNameComponent}__item-body`}>
											<div>
												<p className={`${classNameComponent}__item-text`}>
													Autor: <span>{item.autor}</span>
												</p>
												<p className={`${classNameComponent}__item-text`}>
													Año: <span>{item.year}</span>
												</p>
												<p className={`${classNameComponent}__item-text`}>
													Tomo: <span>{item.tomo}</span>
												</p>
											</div>
											<div>
												{item.file?.url && (
													<a
														href={item.file.url}
														className={`${classNameComponent}__item-icon`}
														target="_blank"
														rel="noreferrer"
													>
														<CTNIcon icon="pdfDuotone" />
														<span className={`${classNameComponent}__item-text`}>
															Descargar Tomo
														</span>
													</a>
												)}
											</div>
										</div>
									</li>
								))}
							</ul>

							{pagination && (
								<nav className="c-pagination u-mt-16" aria-label="Paginación">
									<CTNPagination
										ref={paginationRef}
										pageSize={pageSize}
										totalData={pagination.total ?? 0}
										locales={{
											text1: 'Página',
											text2: 'de',
											text3: 'Mostrando registros {0} de {1}',
										}}
										onPageChange={handlePageChange}
									/>
								</nav>
							)}
						</>
					)}
				</>
			)}
		</section>
	);
};
